import { useState, useEffect, useRef } from 'react'
import type { StepperItem } from './index'

interface UseStepperOptions {
  items: StepperItem[]
  currentStep: number
  dynamicHeight?: boolean
}

export function useStepper({
  items,
  currentStep,
  dynamicHeight = false,
}: UseStepperOptions) {
  const step = Math.max(0, Math.min(currentStep, items.length - 1))
  const currentLabel = (items[step] as StepperItem).label

  const panelRefs = useRef<(HTMLDivElement | undefined)[]>([])
  const [height, setHeight] = useState<number | undefined>(undefined)
  const [isSliding, setIsSliding] = useState(false)
  const isMounted = useRef(false)

  useEffect(() => {
    if (!dynamicHeight) return
    const panel = panelRefs.current[step]
    if (!panel) return

    const observer = new ResizeObserver(() => {
      setHeight(panel.scrollHeight)
    })
    observer.observe(panel)
    setHeight(panel.scrollHeight)

    return () => observer.disconnect()
  }, [step, dynamicHeight])

  useEffect(() => {
    if (!dynamicHeight) return
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    setIsSliding(true)
  }, [step, dynamicHeight])

  function handleTransitionEnd(propertyName: string) {
    if (dynamicHeight && propertyName === 'transform') {
      setIsSliding(false)
    }
  }

  return {
    step,
    currentLabel,
    panelRefs,
    height,
    isSliding,
    handleTransitionEnd,
  }
}
